import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import app, { authenticateFirestoreClient, defaultAuth, loginVerifierAuth } from "../../firebase";
import { validateActiveAccount } from "../../authStatus";
import LoginErrorMessage from "../common/LoginErrorMessage";
import { assertRoleLoginAllowed, getRoleDashboardPath, usePortalSessionState } from "../../auth/roleSession";

const db = getFirestore(app);

function getFriendlyAuthError(error) {
  switch (error?.code) {
    case "auth/invalid-credential":
    case "auth/invalid-login-credentials":
    case "auth/wrong-password":
    case "auth/user-not-found":
    case "auth/user-disabled":
      return "Incorrect email or password.";
    case "auth/invalid-email":
      return "Enter a valid email address.";
    case "auth/too-many-requests":
      return "Too many failed attempts. Try again later.";
    case "auth/network-request-failed":
    case "unavailable":
    case "deadline-exceeded":
      return "Service temporarily unavailable. Check your connection and try again.";
    case "auth/role-already-active":
      return error.message;
    default:
      return "Unable to sign in. Please try again.";
  }
}

const getRoleSessionKey = (role) => {
  if (role === "Registrar") return "registrarPortalLogin";
  if (role === "Admission Counselor") return "admissionPortalLogin";
  if (role === "Room Manager") return "roomPortalLogin";
  return "servicesPortalLogin";
};

export default function ServicesLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { loading: sessionLoading, role: activeRole } = usePortalSessionState(["Registrar", "Admission Counselor", "Finance", "Room Manager"]);

  useEffect(() => {
    if (!sessionLoading && activeRole) navigate(getRoleDashboardPath(activeRole), { replace: true });
  }, [activeRole, navigate, sessionLoading]);

  if (sessionLoading) return null;
  const redirectByRole = (role) => {
    if (role === "Registrar") {
      navigate("/registar/dashboard", { replace: true });
    } else if (role === "Admission Counselor") {
      navigate("/admission/dashboard", { replace: true });
    } else if (role === "Treasurer" || role === "Financial Officer") {
      navigate("/finances/dashboard", { replace: true });
    } else if (role === "Room Manager") {
      navigate("/room-management", { replace: true });
    }
  };


  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail || !password) {
      setError("Please enter both email and password.");
      return;
    }

    setLoading(true);
    let persistentAuth = null;
    let createdPersistentSession = false;
    let loginSucceeded = false;
    let firestoreAuthUid = null;
    try {
      const verifiedCredential = await signInWithEmailAndPassword(loginVerifierAuth, normalizedEmail, password);
      const uid = verifiedCredential.user.uid;
      await authenticateFirestoreClient(normalizedEmail, password, uid);
      firestoreAuthUid = uid;
      const verifiedProfile = await getDoc(doc(db, "users", uid));
      if (!verifiedProfile.exists()) {
        setError("Unable to sign in. Please try again.");
        return;
      }

      const verifiedData = verifiedProfile.data();
      const validation = validateActiveAccount(verifiedData, "Account");
      if (!validation.active) {
        setError(validation.message);
        return;
      }

      const role = verifiedData.role;
      const allowedRoles = ["Registrar", "Admission Counselor", "Treasurer", "Financial Officer", "Room Manager"];
      if (!allowedRoles.includes(role)) {
        setError("You are signing in to a different role. Please choose the correct login page for your role from the home page.");
        return;
      }

      const authRole = role === "Treasurer" || role === "Financial Officer" ? "Finance" : role;
      persistentAuth = await assertRoleLoginAllowed(authRole, uid);
      if (persistentAuth.currentUser?.uid !== uid) {
        const persistentCredential = await signInWithEmailAndPassword(persistentAuth, normalizedEmail, password);
        createdPersistentSession = true;
        if (persistentCredential.user.uid !== uid) {
          await signOut(persistentAuth);
          createdPersistentSession = false;
          setError("Unable to sign in. Please try again.");
          return;
        }
      }

      const userDoc = await getDoc(doc(db, "users", uid));
      if (!userDoc.exists()) {
        setError("Unable to sign in. Please try again.");
        return;
      }

      const persistentData = userDoc.data();
      const persistentValidation = validateActiveAccount(persistentData, "Account");
      if (!persistentValidation.active) {
        setError(persistentValidation.message);
        if (persistentAuth.currentUser?.uid === uid) {
          await signOut(persistentAuth);
          createdPersistentSession = false;
        }
        return;
      }

      if (persistentData.role !== role) {
        setError("You are signing in to a different role. Please choose the correct login page for your role from the home page.");
        if (createdPersistentSession) {
          await signOut(persistentAuth);
          createdPersistentSession = false;
        }
        return;
      }

      console.log("[ServicesPortal] Login successful with role:", role);

      const sessionKey = getRoleSessionKey(role);
      const roleKey = `${sessionKey}Role`;

      sessionStorage.setItem("servicesPortalRole", role);
      sessionStorage.setItem(roleKey, role);
      sessionStorage.setItem(sessionKey, "true");

      redirectByRole(role);
      loginSucceeded = true;
    } catch (err) {
      console.error("[ServicesPortal] Login error:", err);
      setError(getFriendlyAuthError(err));
    } finally {
      try {
        if (!loginSucceeded && firestoreAuthUid && defaultAuth.currentUser?.uid === firestoreAuthUid) {
          await signOut(defaultAuth);
        }
      } catch (cleanupError) {
        console.error("Failed to clean up unsuccessful Firestore login session", cleanupError);
      }
      try {
        if (createdPersistentSession && !loginSucceeded && persistentAuth) {
          await signOut(persistentAuth);
        }
      } catch (cleanupError) {
        console.error("Failed to clean up unsuccessful Services login session", cleanupError);
      }
      try {
        await signOut(loginVerifierAuth);
      } catch (cleanupError) {
        console.error("Failed to clean up login verifier session", cleanupError);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(90deg, #009966 0%, #155c2b 100%)",
      padding: 20,
      boxSizing: "border-box"
    }}>
      <div style={{
        width: "100%",
        maxWidth: 420,
        background: "#fff",
        borderRadius: 24,
        boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
        padding: 36,
        boxSizing: "border-box"
      }}>
        <h2 style={{ textAlign: "center", fontWeight: 800, color: "#155c2b", marginBottom: 8 }}>EnrollEase</h2>
        <p style={{ textAlign: "center", color: "#666", fontSize: 14, marginBottom: 24 }}>Staff Services Portal</p>

        <form onSubmit={handleLogin}>
          <label style={{ display: "block", fontWeight: 600, color: "#155c2b", marginBottom: 6 }}>Email</label>
          <input
            type="email"
            placeholder="you@school.edu.ph"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            style={{ width: "100%", marginBottom: 12, padding: "12px 14px", borderRadius: 8, border: "1px solid #d1d5db", boxSizing: "border-box" }}
            autoComplete="username"
          />

          <label style={{ display: "block", fontWeight: 600, color: "#155c2b", marginBottom: 6 }}>Password</label>
          <input
            type="password"
            placeholder="Enter your password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            style={{ width: "100%", marginBottom: 12, padding: "12px 14px", borderRadius: 8, border: "1px solid #d1d5db", boxSizing: "border-box" }}
            autoComplete="current-password"
          />

          <div style={{ textAlign: "right", marginBottom: 12 }}>
            <Link to="/forgot-password?scope=services&returnTo=%2Fservices" style={{ color: "#155c2b", fontSize: 14 }}>Forgot Password?</Link>
          </div>

          <LoginErrorMessage message={error} />

          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              padding: "12px 0",
              background: loading ? "#ccc" : "#155c2b",
              color: "#fff",
              border: "none",
              borderRadius: 10,
              fontWeight: 700,
              fontSize: 16,
              cursor: loading ? "not-allowed" : "pointer",
              marginBottom: 12
            }}
          >
            {loading ? "Signing in..." : "LOG IN"}
          </button>

          <div style={{ textAlign: "center", marginTop: 20, fontSize: 13, color: "#666" }}>
            <p>Access for:</p>
            <p style={{ marginBottom: 4 }}>Registrar • Admission Counselor • Room Manager</p>
          </div>
        </form>
        <div style={{ textAlign: "center", marginTop: 8 }}>
          <Link to="/" style={{ color: "#155c2b", fontSize: 14 }}>Return to home</Link>
        </div>
      </div>
    </div>
  );
}
